/** Text encoding and decoding (Base64, URL, HTML entities, Unicode escapes) behind the Text Encoder / Decoder. */

/* ------------------------------------------------------------------ */
/*  Regex at module level (Biome rule)                                */
/* ------------------------------------------------------------------ */

const RE_HTML_ENTITIES = /&#(\d+);|&#x([0-9a-fA-F]+);|&(\w+);/g;
const RE_UNICODE_ESCAPE = /\\u([0-9a-fA-F]{4})/g;
const RE_WHITESPACE = /\s+/g;
const RE_URL_SAFE_MINUS = /-/g;
const RE_URL_SAFE_UNDERSCORE = /_/g;
const RE_PADDING = /=+$/;
const MAX_CODE_POINT = 0x10_ff_ff;

/* ------------------------------------------------------------------ */
/*  Encoding/Decoding logic                                           */
/* ------------------------------------------------------------------ */

export type EncodingFormat = "base64" | "url" | "html" | "unicode";
export type Direction = "encode" | "decode";

export const FORMAT_VALUES: EncodingFormat[] = [
  "base64",
  "url",
  "html",
  "unicode",
];

const HTML_ENTITY_MAP: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  copy: "©",
  reg: "®",
  trade: "™",
  euro: "€",
  pound: "£",
  yen: "¥",
  cent: "¢",
  mdash: "—",
  ndash: "–",
  laquo: "«",
  raquo: "»",
  hellip: "…",
};

function encodeBase64(text: string): string {
  try {
    return btoa(unescape(encodeURIComponent(text)));
  } catch {
    return "[Error: could not encode to Base64]";
  }
}

/**
 * Standard or URL-safe Base64 (`-` and `_` in place of `+` and `/`, padding optional)
 * as the padded standard alphabet `atob` expects; null when the length cannot be Base64.
 */
export function normalizeBase64(text: string): string | null {
  const body = text
    .replace(RE_WHITESPACE, "")
    .replace(RE_URL_SAFE_MINUS, "+")
    .replace(RE_URL_SAFE_UNDERSCORE, "/")
    .replace(RE_PADDING, "");
  if (body.length % 4 === 1) {
    return null;
  }
  return body + "=".repeat((4 - (body.length % 4)) % 4);
}

function decodeBase64(text: string): string {
  try {
    const normalized = normalizeBase64(text);
    if (normalized === null) {
      return "[Error: invalid Base64 input]";
    }
    return decodeURIComponent(escape(atob(normalized)));
  } catch {
    return "[Error: invalid Base64 input]";
  }
}

function encodeUrl(text: string): string {
  try {
    return encodeURIComponent(text);
  } catch {
    return "[Error: could not URL-encode]";
  }
}

function decodeUrl(text: string): string {
  try {
    return decodeURIComponent(text.trim());
  } catch {
    return "[Error: invalid URL-encoded input]";
  }
}

function encodeHtml(text: string): string {
  // By code point, so an emoji becomes one reference instead of two lone surrogates.
  return Array.from(text)
    .map((char) => {
      const code = char.codePointAt(0) ?? 0;
      if (
        code > 127 ||
        char === "&" ||
        char === "<" ||
        char === ">" ||
        char === '"' ||
        char === "'"
      ) {
        return `&#${code};`;
      }
      return char;
    })
    .join("");
}

function fromCodePoint(code: number, fallback: string): string {
  return code > 0 && code <= MAX_CODE_POINT
    ? String.fromCodePoint(code)
    : fallback;
}

function decodeHtml(text: string): string {
  return text.replace(RE_HTML_ENTITIES, (match, decimal, hex, named) => {
    if (decimal) {
      return fromCodePoint(Number.parseInt(decimal, 10), match);
    }
    if (hex) {
      return fromCodePoint(Number.parseInt(hex, 16), match);
    }
    if (named && HTML_ENTITY_MAP[named]) {
      return HTML_ENTITY_MAP[named];
    }
    return match;
  });
}

function encodeUnicode(text: string): string {
  return text
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0);
      if (code > 127) {
        return `\\u${code.toString(16).padStart(4, "0")}`;
      }
      return char;
    })
    .join("");
}

function decodeUnicode(text: string): string {
  return text.replace(RE_UNICODE_ESCAPE, (_, hex) => {
    return String.fromCharCode(Number.parseInt(hex, 16));
  });
}

export function processText(
  input: string,
  format: EncodingFormat,
  direction: Direction
): string {
  if (input.trim().length === 0) {
    return "";
  }

  if (direction === "encode") {
    switch (format) {
      case "base64":
        return encodeBase64(input);
      case "url":
        return encodeUrl(input);
      case "html":
        return encodeHtml(input);
      case "unicode":
        return encodeUnicode(input);
      default:
        return input;
    }
  }

  switch (format) {
    case "base64":
      return decodeBase64(input);
    case "url":
      return decodeUrl(input);
    case "html":
      return decodeHtml(input);
    case "unicode":
      return decodeUnicode(input);
    default:
      return input;
  }
}
